/**
 * U-02 «Предпросмотр: как меня видят другие».
 *
 * ПС §07: карточка в формате P-04 — только `approved`-фото и публичные поля.
 * Приватное сюда НЕ попадает: статусы модерации, заполненность, пауза,
 * источник гео, отклонённые и ожидающие проверки фото и тексты.
 *
 * Изменение v1.5: экран собран из тех же блоков, что P-04 и U-01
 * (`entities/user/ProfileView`), поэтому «как меня видят» — это буквально
 * чужой профиль, а не его пересказ другой вёрсткой.
 *
 * Экран показывает карточку и ничего не объясняет словами: заголовок «Как меня
 * видят» уже всё сказал. Строкой сверху отмечается только то, чего в карточке
 * не видно, — что анкету сейчас не показывают или что поля ещё на проверке.
 *
 * Данные берутся из уже загруженного `GET /me` — отдельного запроса нет
 * (проекция считается по тем же правилам, что и `GET /users/{userId}`).
 */
import { useNavigate } from '@tanstack/react-router'

import { RHYTHM_ICON, RHYTHM_LABEL } from '@entities/candidate/vibe'
import {
  ProfileChips,
  ProfileHero,
  ProfileInfo,
  ProfileInfoRow,
  ProfileSection,
} from '@entities/user/ProfileView'
import { profileVisibility } from '@entities/user/visibility'
import type { FieldModerationState } from '@shared/api/types'
import { Button, Chip, Icon, cx } from '@shared/ui'

import { Screen } from '../../layouts/Screen'
import { useAppStore } from '../../model/app-store'
import styles from './preview.module.css'

/**
 * BS §14.2: публичным становится только `approved`-значение. Пока поле
 * на проверке или отклонено, другие видят последнее одобренное или ничего —
 * предпросмотр обязан показывать именно это, иначе он врёт.
 */
function publicValue(
  value: string | null | undefined,
  state: FieldModerationState | undefined,
): string | null {
  if (!value) return null
  return state?.state === 'approved' ? value : null
}

export function ProfilePreviewScreen(): React.ReactElement {
  const navigate = useNavigate()
  const me = useAppStore((s) => s.me)

  if (!me)
    return (
      <Screen title="Как меня видят" back narrow>
        {null}
      </Screen>
    )

  const profile = me.profile
  const photos = profile.photos.filter((photo) => photo.state === 'approved')
  // Изменение v1.3: единственный режим — friends («Движ»)
  const mode = me.modes.find((m) => m.mode === 'friends' && m.enabled) ?? null

  const displayName = publicValue(profile.display_name, profile.field_states.display_name)
  const bio = publicValue(profile.bio, profile.field_states.bio)
  const occupation = publicValue(profile.occupation, profile.field_states.occupation)

  const hiddenFields = [
    profile.display_name && !displayName ? 'имя' : null,
    profile.bio && !bio ? 'о себе' : null,
    profile.occupation && !occupation ? 'род занятий' : null,
  ].filter(Boolean) as string[]

  const visibility = profileVisibility(me)

  return (
    <Screen
      title="Как меня видят"
      back
      narrow
      actions={
        <Button variant="ghost" size="sm" onClick={() => void navigate({ to: '/me/edit' })}>
          Изменить
        </Button>
      }
    >
      {!visibility.visible ? (
        <p className={cx(styles['notice'], styles['noticeWarning'])}>
          <Icon name="eye-off" size={16} />
          Сейчас анкету не показывают: {visibility.title.toLowerCase()}
        </p>
      ) : null}

      {photos.length === 0 ? (
        <div className={styles['noPhoto']}>
          <Icon name="image" size={28} />
          <span>Нет проверенного фото — карточку не покажут</span>
          <Button variant="primary" size="sm" onClick={() => void navigate({ to: '/me/photos' })}>
            Добавить фото
          </Button>
        </div>
      ) : null}

      <ProfileHero name={displayName} age={profile.age} photos={photos} />

      {profile.username || bio || profile.city || occupation || profile.languages.length ? (
        <ProfileInfo>
          {profile.username ? <ProfileInfoRow label="никнейм" value={`@${profile.username}`} accent /> : null}
          {bio ? <ProfileInfoRow label="о себе" value={bio} multiline /> : null}
          {profile.city ? <ProfileInfoRow label="город" value={profile.city.name} /> : null}
          {occupation ? <ProfileInfoRow label="род занятий" value={occupation} /> : null}
          {profile.languages.length ? (
            <ProfileInfoRow label="языки" value={profile.languages.join(', ')} />
          ) : null}
        </ProfileInfo>
      ) : null}

      {profile.interests.length ? (
        <ProfileSection title="Интересы">
          <ProfileChips>
            {profile.interests.map((interest) => (
              <Chip key={interest.id} static>
                {interest.label}
              </Chip>
            ))}
          </ProfileChips>
        </ProfileSection>
      ) : null}

      {mode?.formats?.length ? (
        <ProfileSection title="Какой движ">
          <ProfileChips>
            {mode.formats.map((format) => (
              <Chip key={format} static>
                {format}
              </Chip>
            ))}
          </ProfileChips>
        </ProfileSection>
      ) : null}

      {mode?.rhythm?.length ? (
        <ProfileSection title="Когда свободно">
          <ProfileChips>
            {mode.rhythm.map((slot) => (
              <Chip key={slot} static icon={RHYTHM_ICON[slot]}>
                {RHYTHM_LABEL[slot]}
              </Chip>
            ))}
          </ProfileChips>
        </ProfileSection>
      ) : null}

      {hiddenFields.length ? (
        <p className={styles['footnote']}>Снято проверкой и не видно другим: {hiddenFields.join(', ')}.</p>
      ) : null}
    </Screen>
  )
}
